/**
 * How Marketing › Advertising words and formats what the overview and reconciliation
 * routes send: each KPI's label and polarity, every `null_reason` / `not_available` the
 * READMEs list, and the signed gaps. A null is always worded, never shown as 0.
 */

import type {
  AdKpi,
  AdKpiKey,
  AdKpiNullReason,
  AdNotAvailable,
  DiffNullReason,
  MetaNotAvailable,
  ReconciliationLineKind,
} from '@/api/advertisingOverview'
import { formatCurrency, formatInteger, formatPercent, formatRatio, type Polarity } from '@/design'
import { formatMoney } from '@/features/analytics/advertising/metrics'

/** "—": a figure undefined here, never a 0 (the competition detail page's `UNDEFINED_FIGURE`). */
export const UNDEFINED_FIGURE = '—'

const MINUS = '−'

/** Every label but revenue per ad spend's, which names the report's currency: every consumer reads `kpiLabel`. */
const KPI_LABELS: Record<Exclude<AdKpiKey, 'revenue_per_ad_spend'>, string> = {
  ad_spend: 'Ad spend',
  attributed_revenue: 'Attributed revenue',
  roas: 'ROAS',
  purchases: 'Purchases',
  cpa: 'CPA',
  cost_per_new_customer: 'Cost per new customer',
  ctr: 'CTR',
  cpc: 'CPC',
  cpm: 'CPM',
}

/** Whether a rise is good news: revenue and ROAS up are, costs up are not, spend is neither. */
export const KPI_POLARITY: Record<AdKpiKey, Polarity> = {
  ad_spend: 'neutral',
  attributed_revenue: 'up_good',
  roas: 'up_good',
  revenue_per_ad_spend: 'up_good',
  purchases: 'up_good',
  cpa: 'down_good',
  cost_per_new_customer: 'down_good',
  ctr: 'up_good',
  cpc: 'down_good',
  cpm: 'down_good',
}

/** A reported KPI's null `current`, in words (`advertising_overview/README.md`, *null_reason*). */
export const KPI_NULL_REASON_TEXT: Record<AdKpiNullReason, string> = {
  no_spend: 'No ad spend in the period',
  sales_currency_rate_unavailable: 'A sale in another currency could not be converted',
  no_attributed_purchases: 'Spend, but no purchase attributed to it',
  no_attributed_new_customers: 'Spend, but no new customer attributed to it',
  no_impressions: 'No impressions in the period',
  no_clicks: 'No clicks in the period',
}

/** A figure with no value at all: a short label and the sentence that explains it. */
export const NOT_AVAILABLE_TEXT: Record<AdNotAvailable, { label: string; why: string }> = {
  needs_ad_platform: {
    label: 'Needs a connected ad platform',
    why: 'No Meta spend was imported for this range. Connect Meta and map an ad account to a website, or choose a range the import covers.',
  },
}

/** Why Meta's side of a reconciliation figure is null (`ad_attribution_reconciliation/README.md`). */
export const META_NOT_AVAILABLE_TEXT: Record<MetaNotAvailable, { label: string; why: string }> = {
  attribution_window_unknown: {
    label: 'Not available',
    why: 'Some of these Meta rows were imported before the attribution window was recorded, so what Meta counted them under is unknown and they are not put beside TmT’s figures.',
  },
  not_a_meta_object: {
    label: 'No Meta figure',
    why: 'These are TmT-attributed purchases no Meta object at this level claims; Meta reports nothing that could stand beside them.',
  },
  not_imported: {
    label: 'Not imported',
    why: 'This object’s ad account is not mapped to a website, or Meta’s hierarchy no longer holds it, so Meta’s figures for it were never imported.',
  },
}

/** Why a gap, or its percentage, is null. */
export const DIFF_NULL_TEXT: Record<DiffNullReason, { label: string; why: string }> = {
  meta_not_available: { label: 'Not available', why: 'There is no Meta figure to compare against.' },
  meta_zero: { label: 'No %', why: 'Meta reported 0, so a difference from it has no percentage.' },
}

/** The label for `total.meta_only`. Not "views and other devices": booking day, timezone and omni_purchase add to it too. */
export const META_ONLY_LABEL = 'Meta counts we can’t check'

/** The TmT-only lines' names; "Paid, no Meta object" is the API's own wording in its `unlinked_visits` cause. */
export const LINE_LABELS: Record<ReconciliationLineKind, string> = {
  tmt_not_at_level: 'TmT-attributed, no Meta object at this level',
  tmt_unmatched_paid: 'Paid, no Meta object',
}

export const LINE_DESCRIPTIONS: Record<ReconciliationLineKind, string> = {
  tmt_not_at_level:
    'Purchases whose visit resolved to a Meta object above this level — a campaign with no ad set, or an ad set with no ad.',
  tmt_unmatched_paid:
    'Purchases whose visit was paid but resolved to no Meta object at all. Any paid platform’s click is counted here, not only Meta’s.',
}

/** What the all-sales figures say about themselves, wherever they are shown: they are not attribution. */
export const ALL_SALES_CAVEAT = 'All sales ÷ ad spend — includes sales the ads didn’t drive'

/** One unit of `currency`, as a reader writes it: "£1", "€1". */
export function oneUnit(currency: string): string {
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency, minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(1)
}

/** A KPI's label in the report's currency: "Revenue per £1 of Meta spend" says £ only for a GBP report. */
export function kpiLabel(key: AdKpiKey, currency: string): string {
  return key === 'revenue_per_ad_spend' ? `Revenue per ${oneUnit(currency)} of Meta spend` : KPI_LABELS[key]
}

/** One KPI's `current` (or `previous`) as display text, in the report's currency. */
export function formatKpiValue(kpi: Pick<AdKpi, 'key' | 'unit'>, value: number, currency: string): string {
  switch (kpi.unit) {
    case 'currency_minor':
      return formatMoney(value, currency)
    case 'count':
      return formatInteger(value)
    case 'ratio':
      // CTR is a rate of impressions; ROAS a multiple of spend; revenue per ad spend is
      // money per one unit of spend, so it reads as money: "£2.25".
      if (kpi.key === 'ctr') return formatPercent(value, 2)
      if (kpi.key === 'revenue_per_ad_spend') return formatCurrency(value, { currency })
      return formatRatio(value)
  }
}

function sign(value: number): string {
  return value > 0 ? '+' : value < 0 ? MINUS : ''
}

/** A signed count: "+3", "−4", "0". */
export function formatSignedCount(value: number): string {
  return `${sign(value)}${formatInteger(Math.abs(value))}`
}

/** A signed amount of money: "+£12.00", "−£88.00". */
export function formatSignedMoney(minor: number, currency: string): string {
  return `${sign(minor)}${formatMoney(Math.abs(minor), currency)}`
}

/** A signed percent the API already multiplied by 100: −12.9 → "−12.90%". */
export function formatSignedPct(pct: number): string {
  return `${sign(pct)}${Math.abs(pct).toFixed(2)}%`
}

/** Meta's attribution windows as a reader writes them: `7d_click,1d_view` → "7-day click + 1-day view". */
export function windowsLabel(windows: readonly string[]): string {
  if (windows.length === 0) return 'window not recorded'
  return windows
    .map((window) =>
      window
        .split(',')
        .map((part) => part.trim().replace(/^(\d+)d_(click|view)$/, '$1-day $2'))
        .join(' + '),
    )
    .join(' | ')
}
